import {CheckCircle, ChevronDown, ChevronRight, Info, MinusCircle, XCircle} from 'lucide-react';
import {useState} from 'react';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@common-web/components/ui/tooltip';
import {
  INTERNAL_MENU_FLAGS,
  INTERNAL_MENU_SECTIONS,
  resolveInternalMenuItemEnabled,
  type InternalFeaturesShape,
  type InternalMenuFlagDefinition,
  type InternalMenuKey,
  type InternalMenuSection,
} from '@common/menuFlags';

export type OverrideState = 'default' | 'on' | 'off';

function overrideStateFor(
  features: InternalFeaturesShape | null | undefined,
  key: InternalMenuKey,
): OverrideState {
  const override = features?.menuOverrides?.[key];
  if (override === true) return 'on';
  if (override === false) return 'off';
  return 'default';
}

function defaultLabel(flag: InternalMenuFlagDefinition): string {
  return flag.defaultVisible ? 'Default: visible' : 'Default: hidden';
}

type InternalFeatureFlagControlsProps = {
  features: InternalFeaturesShape;
  disabled?: boolean;
  onOverrideChange: (key: InternalMenuKey, next: OverrideState) => void;
};

export function InternalFeatureFlagControls({
  features,
  disabled,
  onOverrideChange,
}: InternalFeatureFlagControlsProps) {
  return (
    <div className='space-y-6'>
      {INTERNAL_MENU_SECTIONS.map((section) => (
        <SectionGroup
          key={section}
          section={section}
          features={features}
          disabled={disabled}
          onOverrideChange={onOverrideChange}
        />
      ))}
    </div>
  );
}

function SectionGroup({
  section,
  features,
  disabled,
  onOverrideChange,
}: {
  section: InternalMenuSection;
  features: InternalFeaturesShape;
  disabled?: boolean;
  onOverrideChange: (key: InternalMenuKey, next: OverrideState) => void;
}) {
  const [open, setOpen] = useState(section === 'General');
  const flags = INTERNAL_MENU_FLAGS.filter((f) => f.section === section);

  const visibleCount = flags.filter((f) => resolveInternalMenuItemEnabled(f.key, features)).length;
  const overrideCount = flags.filter((f) => overrideStateFor(features, f.key) !== 'default').length;

  return (
    <div className='border border-gray-200 rounded-lg overflow-hidden bg-white'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className='w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left'
      >
        <div className='flex items-center gap-2'>
          {open ? (
            <ChevronDown className='h-4 w-4 text-brand-gray' />
          ) : (
            <ChevronRight className='h-4 w-4 text-brand-gray' />
          )}
          <div className='text-sm font-semibold text-brand-navy'>{section}</div>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <Badge variant='outline' className='text-xs'>
            {visibleCount} / {flags.length} visible
          </Badge>
          {overrideCount > 0 && (
            <Badge className='bg-amber/10 text-amber border-amber/20 text-xs'>
              {overrideCount} override{overrideCount === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
      </button>

      {open && (
        <div className='p-4 space-y-3'>
          {flags.map((flag) => (
            <FlagOverrideRow
              key={flag.key}
              flag={flag}
              state={overrideStateFor(features, flag.key)}
              effective={resolveInternalMenuItemEnabled(flag.key, features)}
              disabled={disabled}
              onChange={(next) => onOverrideChange(flag.key, next)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FlagOverrideRow({
  flag,
  state,
  effective,
  disabled,
  onChange,
}: {
  flag: InternalMenuFlagDefinition;
  state: OverrideState;
  effective: boolean;
  disabled?: boolean;
  onChange: (next: OverrideState) => void;
}) {
  return (
    <div className='flex items-center justify-between gap-4 py-2'>
      <div className='min-w-0'>
        <div className='flex items-center gap-2'>
          <div className='text-sm font-medium text-brand-navy truncate'>{flag.label}</div>
          {flag.description && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className='h-3.5 w-3.5 text-brand-gray shrink-0' />
                </TooltipTrigger>
                <TooltipContent className='max-w-xs'>{flag.description}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className='text-xs text-brand-gray flex items-center gap-2'>
          <span>{defaultLabel(flag)}</span>
          <span className='text-gray-300'>·</span>
          <span className={effective ? 'text-emerald' : 'text-red-500'}>
            Effective: {effective ? 'visible' : 'hidden'}
          </span>
        </div>
      </div>

      <div className='flex items-center gap-1 shrink-0'>
        <OverrideButton
          label='Default'
          icon={<MinusCircle className='h-3.5 w-3.5' />}
          active={state === 'default'}
          disabled={disabled}
          onClick={() => onChange('default')}
        />
        <OverrideButton
          label='On'
          icon={<CheckCircle className='h-3.5 w-3.5' />}
          active={state === 'on'}
          disabled={disabled}
          onClick={() => onChange('on')}
        />
        <OverrideButton
          label='Off'
          icon={<XCircle className='h-3.5 w-3.5' />}
          active={state === 'off'}
          disabled={disabled}
          onClick={() => onChange('off')}
        />
      </div>
    </div>
  );
}

function OverrideButton({
  label,
  icon,
  active,
  disabled,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type='button'
      variant={active ? 'default' : 'outline'}
      size='sm'
      disabled={disabled}
      onClick={onClick}
      className='h-7 px-2 gap-1 text-xs'
    >
      {icon}
      {label}
    </Button>
  );
}
